if (!customElements.get('bundle-section')) {
  class BundleSection extends HTMLElement {
    constructor() {
      super();
      this.initSwiper = this.initSwiper.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
      this.sectionID = this.dataset.sectionId;
      this.swiperEl = null;
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.setupEventListeners();
        this.initSwiper();
      });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.initSwiper);
      }
    }

    disconnectedCallback() {
      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.initSwiper);
      }
    }

    setupEventListeners() {
      const addButtons = this.querySelectorAll('.add-bundle-to-cart-btn');
      addButtons.forEach((button) => {
        button.addEventListener('click', this.handleAddToCart);
      });
    }

    showQuickAdd() {
      document.dispatchEvent(
        new CustomEvent('show-quick-add', { detail: { sectionId: this.sectionID } })
      );
    }

    hideQuickAdd() {
      document.dispatchEvent(
        new CustomEvent('hide-quick-add', { detail: { sectionId: this.sectionID } })
      );
    }

    async fetchVariantStock(variantIds) {
      const domain = window.huntsman.domain;
      const token = window.huntsman.storeFrontApi;

      const query = `
        query getVariantStock($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              title
              quantityAvailable
              product {
                title
              }
            }
          }
        }`;

      const response = await fetch(`https://${domain}/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables: { ids: variantIds } }),
      });

      const data = await response.json();

      return data.data.nodes;
    }

    /**
     * Ensure all product variants have been selected in the bundle.
     * Slides to the first incomplete item if not.
     */
    ensureAllVariantsSelected(productsArray) {
      if (productsArray.some((p) => !p.value)) {
        const swiper = this.swiperEl?.swiper;
        if (!swiper) return false;

        const lastIndex = productsArray.length - 1;
        const lastValueEmpty = !productsArray[lastIndex].value;

        if (lastValueEmpty && !this.isSlideActive(lastIndex)) {
          swiper.slideTo(lastIndex);
        } else if (!this.isSlideActive(0)) {
          swiper.slideTo(0);
        }

        return false;
      }

      return true;
    }

    /**
     * Fetches variant stock data and returns
     * { validItems: [], skippedItems: [] }
     */
    async checkStockForItems(items) {
      const variantIds = items.map((i) => `gid://shopify/ProductVariant/${i.id}`);
      const variants = await this.fetchVariantStock(variantIds);
      const cart = await fetch('/cart.js').then((r) => r.json());

      const validItems = [];
      const skippedItems = [];

      for (const item of items) {
        const variant = variants.find((v) => v.id.endsWith(item.id));
        const inCartQty = cart.items.find((ci) => ci.id === Number(item.id))?.quantity || 0;

        const available = variant.quantityAvailable ?? 0;
        if (inCartQty + item.quantity <= available) {
          validItems.push(item);
        } else {
          skippedItems.push({
            title: variant.title,
            productTitle: variant.product.title,
            remaining: available - inCartQty,
          });
        }
      }

      return { validItems, skippedItems };
    }

    /**
     * Performs the add-to-cart request for valid items,
     * and shows skipped items info if any.
     */
    async performAddToCart(validItems, skippedItems) {
      const cartNotification = document.querySelector('cart-drawer-flyout');
      if (!validItems.length) {
        this.hideQuickAdd();
        alert('Maximum quantity of all selected sizes have already been added to cart');

        return;
      }

      const request = {
        items: validItems,
        sections: cartNotification.getSectionsToRender().map((s) => s.id),
        sections_url: window.location.pathname,
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      const data = await response.json();
      cartNotification.setActiveElement(document.activeElement);
      cartNotification.renderContents(data);

      if (skippedItems.length) {
        const msg = skippedItems
          .map((s) => `${s.productTitle} / ${s.title}: \n - Max quantity already added to cart`)
          .join('\n');
        alert(`Some items couldn’t be added:\n\n${msg}`);
      }
    }

    /**
     * Main entry point for bundle add-to-cart
     */
    async handleAddToCart() {
      const products = this.querySelectorAll('.bundle-product form input[name="id"]');
      const productsArray = Array.from(products);
      const cartNotification = document.querySelector('cart-drawer-flyout');
      if (!products.length || !cartNotification) return;

      this.showQuickAdd();

      const allSelected = this.ensureAllVariantsSelected(productsArray);
      if (!allSelected) return;

      const items = productsArray.map((p) => ({
        id: p.value,
        quantity: 1,
      }));

      try {
        // Check stock availability
        const { validItems, skippedItems } = await this.checkStockForItems(items);

        await this.performAddToCart(validItems, skippedItems);
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
      }
    }

    initSwiper() {
      this.swiperEl = this.querySelector('.swiper');
      if (!this.swiperEl || this.swiperEl.swiper) return;

      // eslint-disable-next-line no-undef
      new Swiper(this.swiperEl, {
        loop: false,
        spaceBetween: 20,
        slidesPerView: 2,
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true,
        },
        breakpoints: {
          1024: {
            slidesPerView: 3,
            allowTouchMove: false,
          },
        },
      });
    }

    isSlideActive(index) {
      const slides = this.swiperEl.querySelectorAll('.swiper-slide');

      return slides[index].classList.contains('swiper-slide-active');
    }
  }

  customElements.define('bundle-section', BundleSection);
}
